import type {
  GetTemplateArgs,
  PaginationResult,
  Template,
  TemplateDetail,
  TemplateTagCategory,
  TemplateTagResult,
  UseTemplateArgs,
  UseTemplateError,
  UseTemplateResult,
} from '@/typings/template'

import { TemplateDeviceType, TemplateStatus } from '@/typings/template'
import { stringifySearchParams } from '../utils/url'
import { getAuthToken } from './authToken'

type TemplateApiFetchError<T = unknown> = {
  status: number
  message: string
  data?: T
}

type TemplateApiFetchResult<T, ET = unknown> = {
  data?: T
  error?: TemplateApiFetchError<ET>
}

const performFetch = async <T, ET = unknown>(
  url: string,
  init?: RequestInit
): Promise<TemplateApiFetchResult<T, ET>> => {
  const res = await fetch(url, init)
  let resultJson
  try {
    resultJson = (await res.json()) as T & { redirect_url?: string; error?: string }
  } catch (error) {
    console.error('performFetch failed', error)
  }
  if (res.ok) return { data: resultJson }

  return { error: { status: res.status, message: resultJson?.error ?? res.statusText, data: resultJson as ET } }
}

const templateApiFetch = async <T, ET = unknown>(
  url: string,
  init?: RequestInit
): Promise<TemplateApiFetchResult<T, ET>> => {
  if (url.startsWith('/')) {
    url = `${process.env.EXPO_PUBLIC_TEMPLATE_DOMAIN}${url}`
  }

  return performFetch(url, init)
}

export async function getTemplate(templateToken: string): Promise<TemplateApiFetchResult<TemplateDetail>> {
  return templateApiFetch(`/templates/${templateToken}`)
}

export async function getTemplates(args: GetTemplateArgs): Promise<TemplateApiFetchResult<PaginationResult<Template>>> {
  const url = `/query?${stringifySearchParams({
    keywords: args.keywords,
    tag: args.tags?.join(','),
    feeType: 0,
    layout: args.layout,
    sort_by: args.sortBy,
    page: args.page,
    size: args.size,
    status: TemplateStatus.ON_SHELF,
    device_type: TemplateDeviceType.MOBILE,
  })}`

  return templateApiFetch(url)
}

export async function getTags(): Promise<TemplateApiFetchResult<TemplateTagCategory[]>> {
  return templateApiFetch('/tags')
}

export async function useTemplate(
  args: UseTemplateArgs
): Promise<TemplateApiFetchResult<UseTemplateResult, UseTemplateError>> {
  const url = `${process.env.EXPO_PUBLIC_API_BASE_URL}/api/v2/templates/usages?${stringifySearchParams({
    scene: args.scene,
    folder_id: args.folder_id,
  })}`

  const token = getAuthToken()

  return templateApiFetch(url, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: token ?? '' },
    body: JSON.stringify({ tags: args.tags, template_id: args.templateToken }),
  })
}

export async function getTemplateTags(scene: string): Promise<TemplateApiFetchResult<TemplateTagResult[]>> {
  const url = `/tags?scene=${scene}`
  return templateApiFetch(url)
}
